<template>
  <ModalDialog
    ref="dialog"
    :title="title"
    :submitting="submitting"
    :submitTitle="submitTitle"
    :scrollable="false"
    sizeClass="modal-xl"
    icon="fa-border-outer"
    @closed="onClosed"
    @submitted="onSubmitted">

    <LoadingPanel :loading="loading" :loadingError="loadingError" />
    <FormGroup v-bind:form="dialogForm" :labelColumnSize="3" v-if="!loading">
      <FormRow label="Name">
        <FormInputText v-model="edgeworkConfigData.name" field="name" placeholder="Name" />
      </FormRow>
      <FormRow label="Highlight Colour" helpTip="Colour used to highlight panel edges with this edgework selected">
        <FormColourTag v-model="edgeworkConfigData.highlightColour" field="highlightColour" />
      </FormRow>
      <FormRow label="Setting Type" helpTip="Setting type available for entry in the order editor. Eg, angle for mitre cuts">
        <FormSelect :options="OrderEditorEdgeworkSettingTypeLookup" v-model="edgeworkConfigData.settingType" field="settingType" placeholder="Setting Type" />
      </FormRow>

      <FormHorizontalRule />
      <TabbedBox>
        <template v-slot:tablist>
          <Tab :first="true" tabid="tab-stock">
            <i class="far fa-inventory"></i> Stock
          </Tab>
          <Tab tabid="tab-edge-allowance">
            <i class="far fa-ruler"></i> Edge Allowances
          </Tab>
        </template>
        <template v-slot:tabcontent>
          <TabPage :first="true" tabid="tab-stock">
            <ContentTitle>Stock List</ContentTitle>
            <ContentSubTitle>Stock with filter rules to add to the order for edges with this edgework</ContentSubTitle>

            <Scroller :maxHeight="500">
              <StaticDataTable class="static-table-list">
                <template v-slot:header>
                  <tr>
                    <th width="35%">Stock</th>
                    <th v-if="hasNumberSetting"><Tooltip label="Stock filtered by the minimum value of edgework setting. Minimum is inclusive" helpStyle>Value Minimum</Tooltip></th>
                    <th v-if="hasNumberSetting"><Tooltip label="Stock filtered by the maximum value of edgework setting. Maximum is exclusive" helpStyle>Value Maximum</Tooltip></th>
                    <th>Shape Type</th>
                    <th width="10%">Actions</th>
                  </tr>
                </template>
                <template v-slot:body>
                  <tr v-for="edgeworkStock in edgeworkConfigData.orderEditorEdgeworkStocks" :key="edgeworkStock.id">
                    <td>
                      <FormInputStockCode :stockType="InventoryStockTypes.glassService" v-model="edgeworkStock.stockId" placeholder="Edgework Stock" :dropdownAutoWidth="true" :appendToBody="true" />
                    </td>
                    <td v-if="hasNumberSetting">
                      <FormInputText type="number" v-model="edgeworkStock.settingValueFilterMin" placeholder="Minimum Value" />
                    </td>
                    <td v-if="hasNumberSetting">
                      <FormInputText type="number" v-model="edgeworkStock.settingValueFilterMax" placeholder="Maximum Value" />
                    </td>
                    <td>
                      <FormSelect :options="shapePriceTypeOptions" v-model="edgeworkStock.orderEditorShapePriceTypeId" placeholder="Shape Filter" />
                    </td>
                    <td>
                      <ActionDropMenu :tableAction="true">
                        <DropdownMenuItem @click="removeEdgeworkStockItem(edgeworkStock)" icon="fa-trash-alt">Remove</DropdownMenuItem>
                      </ActionDropMenu>
                    </td>
                  </tr>
                </template>
                <template v-slot:empty v-if="edgeworkConfigData.orderEditorEdgeworkStocks.length == 0">No stock items for the edgework found</template>
              </StaticDataTable>
              <button class="btn btn-white btn-xs" @click.prevent="addEdgeworkStockItem"><i class="far fa-plus"></i> Add Stock Item</button>
            </Scroller>
          </TabPage>

          <TabPage tabid="tab-edge-allowance">
            <ContentTitle>Edge Allowance</ContentTitle>
            <ContentSubTitle>Extra allowance of material to add on for edges with this edgework</ContentSubTitle>

            <Scroller :maxHeight="500">
              <StaticDataTable class="static-table-list">
                <template v-slot:header>
                  <tr>
                    <th width="30%">Service Pricing Range</th>
                    <th width="30%">Price Groups</th>
                    <th>Edge Allowance - Straight Line</th>
                    <th>Edge Allowance - Arc</th>
                  </tr>
                </template>
                <template v-slot:body>
                  <tr v-for="servicePriceRange in activeServicePriceRanges" :key="servicePriceRange.id">
                    <td>
                      <strong>{{servicePriceRange.name}}</strong>
                    </td>
                    <td>
                      {{priceRangeGroupListName(servicePriceRange)}}
                    </td>
                    <td>
                      <FormInputText type="number" placeholder="Allowance" addon="mm" :value="edgeAllowanceForPriceRange(servicePriceRange.id)" @input="updateEdgeAllowanceForPriceRange(servicePriceRange.id, $event)" />
                    </td>
                    <td>
                      <FormInputText type="number" placeholder="Arc Allowance" addon="mm" :value="edgeArcAllowanceForPriceRange(servicePriceRange.id)" @input="updateEdgeArcAllowanceForPriceRange(servicePriceRange.id, $event)" />
                    </td>
                  </tr>
                </template>
                <template v-slot:empty v-if="activeServicePriceRanges.length == 0">No price service ranges found</template>
              </StaticDataTable>
            </Scroller>
          </TabPage>
        </template>
      </TabbedBox>
      
    </FormGroup>
  </ModalDialog>
</template>

<script>
import _ from 'lodash'
import { v4 as uuidv4 } from 'uuid'

import form from '@/mixins/form'
import ModalDialog from '@/components/modalDialog.vue'
import modalDialog from '@/mixins/modalDialog'
import FormInputStockCode from '@/modules/inventory/components/formInputStockCode'

import { InventoryStockTypes, OrderEditorEdgeworkSettingTypes, OrderEditorEdgeworkSettingTypeLookup, OrderEditorShapeTypeLookup } from '@/store/types'

export default {
  name: 'EdgeworkConfigDialog',
  mixins: [
    modalDialog,
    form
  ],
  components: {
    ModalDialog,
    FormInputStockCode
  },
  props: {
    isCreateMode: Boolean,
    id: Number,
  },
  data() {
    return {
      dialogForm: {},
      edgeworkConfigData: {
        orderEditorEdgeworkStocks: [],
        orderEditorEdgeworkAllowances: []
      },
      orderEditorShapePriceTypeList: [],
      servicePriceRangeList: [],
      submitting: false,
      loading: false,
      loadingError: ''
    }
  },
  created() {
    this.InventoryStockTypes = InventoryStockTypes
    this.OrderEditorEdgeworkSettingTypes = OrderEditorEdgeworkSettingTypes
    this.OrderEditorEdgeworkSettingTypeLookup = OrderEditorEdgeworkSettingTypeLookup
  },
  mounted() {
    this.loading = true
    this.loadingError = ''

    this.requestData()
    .then(() => {
    })
    .catch(err => {
      console.error(err)
      this.loadingError = 'Error loading data. Check your internet connection and try again'
    })
    .finally(() => {
      this.loading = false
    })
  },
  computed: {
    title() {
      return this.isCreateMode ? 'New Edgework Config Item' : 'Edit Edgework Config Item'
    },
    submitTitle() {
      return this.isCreateMode ? 'Create' : 'Update'
    },
    hasNumberSetting() {
      return this.edgeworkConfigData.settingType != OrderEditorEdgeworkSettingTypes.none
    },
    shapePriceTypeOptions() {
      let priceTypeOptions = this.orderEditorShapePriceTypeList
      .filter(shapePriceType => shapePriceType.isActive)
      .map(shapePriceType => {
        return { name: shapePriceType.name, value: shapePriceType.id }
      })

      return _.concat([{ name: 'All', value: null }], priceTypeOptions)
    },
    activeServicePriceRanges() {
      return this.servicePriceRangeList.filter(range => range.isActive)
    }
  },
  methods: {
    show() {
      // Reset form
      this.edgeworkConfigData = {
        settingType: OrderEditorEdgeworkSettingTypes.none,
        highlightColour: '',
        orderEditorEdgeworkStocks: []
      }
      this.dialogForm = {}
      this.submitting = false

      return modalDialog.methods.show.call(this, arguments)
    },
    onClosed() {
      this.modalClosed(null)
    },
    onSubmitted() {
      this.submitting = true

      var updateData = _.cloneDeep(this.$data.edgeworkConfigData)

      // Cleanup the edgework stock list
      updateData.orderEditorEdgeworkStocks = (updateData.orderEditorEdgeworkStocks || []).map(stockItem => {
        var outputStock = _.cloneDeep(stockItem)
        if(outputStock.isNew)
          delete outputStock.id
        delete outputStock.isNew

        if(!outputStock.orderEditorShapePriceTypeId)
          outputStock.orderEditorShapePriceTypeId = null
          
        return outputStock
      })

      // Cleanup the edgework allowance list
      updateData.orderEditorEdgeworkAllowances = (updateData.orderEditorEdgeworkAllowances || []).map(allowance => {
        var outputAllowance = _.cloneDeep(allowance)
        if(outputAllowance.isNew)
          delete outputAllowance.id
        delete outputAllowance.isNew
          
        return outputAllowance
      })

      if(this.isCreateMode) {
        this.$apiService.orderEditorService.createOrderEditorEdgework(updateData)
        .then(response => {
          this.submitting = false
          this.modalClosed(response.data.id)
          this.hide()
        })
        .catch(err => {
          this.submitting = false
          this.$data.dialogForm = this.processFormResponse(err)
        })
      } else {
        this.$apiService.orderEditorService.updateOrderEditorEdgework(updateData.id, updateData)
        .then(response => {
          this.submitting = false
          this.modalClosed(response.data.id)
          this.hide()
        })
        .catch(err => {
          this.submitting = false
          this.$data.dialogForm = this.processFormResponse(err)
        })
      }
    },
    requestData() {
      return new Promise((resolve, reject) => {

        let requestList = [
          this.$apiService.orderEditorService.getOrderEditorShapePriceTypeList(),
          this.$apiService.pricingService.getServicePriceRangeList()
        ]
        if(!this.isCreateMode && this.id)
          requestList.push(this.$apiService.orderEditorService.getOrderEditorEdgework(this.id))

        Promise.all(requestList)
        .then(results => {
          this.orderEditorShapePriceTypeList = results[0].data
          this.servicePriceRangeList = results[1].data
          if(!this.isCreateMode && this.id)
            this.edgeworkConfigData = results[2].data

          return resolve()
        })
        .catch(err => {
          return reject(err)
        })
      })
    },
    removeEdgeworkStockItem(edgeworkStockItem) {
      this.edgeworkConfigData.orderEditorEdgeworkStocks = this.edgeworkConfigData.orderEditorEdgeworkStocks.filter(item => item.id != edgeworkStockItem.id)
    },
    addEdgeworkStockItem() {
      this.edgeworkConfigData.orderEditorEdgeworkStocks.push({
        id: `temp-id-` + uuidv4(),
        isNew: true,
        stockId: null,
        settingValueFilterMin: 0,
        settingValueFilterMax: 0,
        orderEditorShapePriceTypeId: null
      })
    },

    priceRangeGroupListName(servicePriceRange) {
      return servicePriceRange.stockPriceGroups.map(priceGroup => priceGroup.name).join(', ')
    },
    edgeAllowanceForPriceRange(servicePriceRangeId) {
      if(!this.edgeworkConfigData.orderEditorEdgeworkAllowances)
        return 0

      let allowance = this.edgeworkConfigData.orderEditorEdgeworkAllowances.find(allowance => allowance.servicePriceRangeId == servicePriceRangeId)
      if(allowance)
        return allowance.allowanceAmount
      return 0
    },
    edgeArcAllowanceForPriceRange(servicePriceRangeId) {
      if(!this.edgeworkConfigData.orderEditorEdgeworkAllowances)
        return 0

      let allowance = this.edgeworkConfigData.orderEditorEdgeworkAllowances.find(allowance => allowance.servicePriceRangeId == servicePriceRangeId)
      if(allowance)
        return allowance.allowanceAmountArc
      return 0
    },
    updateEdgeAllowanceForPriceRange(servicePriceRangeId, value) {
      if(!this.edgeworkConfigData.orderEditorEdgeworkAllowances)
        this.edgeworkConfigData.orderEditorEdgeworkAllowances = []

      let allowance = this.edgeworkConfigData.orderEditorEdgeworkAllowances.find(allowance => allowance.servicePriceRangeId == servicePriceRangeId)
      if(allowance)
        allowance.allowanceAmount = value
      else {
        // Add new allowance
        this.edgeworkConfigData.orderEditorEdgeworkAllowances.push({
          id: `temp-id-` + uuidv4(),
          isNew: true,
          servicePriceRangeId: servicePriceRangeId,
          allowanceAmount: value,
          allowanceAmountArc: 0
        })
      }
    },
    updateEdgeArcAllowanceForPriceRange(servicePriceRangeId, value) {
      if(!this.edgeworkConfigData.orderEditorEdgeworkAllowances)
        this.edgeworkConfigData.orderEditorEdgeworkAllowances = []

      let allowance = this.edgeworkConfigData.orderEditorEdgeworkAllowances.find(allowance => allowance.servicePriceRangeId == servicePriceRangeId)
      if(allowance)
        allowance.allowanceAmountArc = value
      else {
        // Add new allowance
        this.edgeworkConfigData.orderEditorEdgeworkAllowances.push({
          id: `temp-id-` + uuidv4(),
          isNew: true,
          servicePriceRangeId: servicePriceRangeId,
          allowanceAmount: 0,
          allowanceAmountArc: value
        })
      }
    }
  }
}
</script>